// ABOUTME: TipTap rich text editor component for report sections.
// ABOUTME: Full formatting toolbar with bold, italic, underline, strikethrough, colors, and font sizes.

import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Node } from '@tiptap/pm/model';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Type,
  Highlighter,
  Undo2,
  Redo2,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  RemoveFormatting,
} from 'lucide-react';
import { ColorPicker } from './ColorPicker';
import { FontSizeDropdown } from './FontSizeDropdown';

export interface SelectionState {
  from: number;
  to: number;
  startOffset: number;
  endOffset: number;
  selectedText: string;
  isEmpty: boolean;
}

export interface RichTextEditorRef {
  getEditor: () => Editor | null;
  getText: () => string;
  getSelection: () => SelectionState | null;
  setHighlight: (range: { start: number; end: number } | null) => void;
}

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  onSelectionChange?: (selection: SelectionState) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  className?: string;
  showToolbar?: boolean;
}

// Custom font size extension using TextStyle
const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
        },
    } as const;
  },
});

// Create a TipTap extension that manages highlight decorations per editor instance
function createMagicEditHighlightExtension(
  getHighlightRange: () => { start: number; end: number } | null
) {
  const highlightPluginKey = new PluginKey('magicEditHighlight');

  return Extension.create({
    name: 'magicEditHighlight',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: highlightPluginKey,
          props: {
            decorations: (state) => {
              const range = getHighlightRange();
              if (!range) return DecorationSet.empty;

              const { doc } = state;
              const { start, end } = range;

              // Convert plain text offsets to ProseMirror positions
              let currentTextOffset = 0;
              let startPos: number | null = null;
              let endPos: number | null = null;

              doc.descendants((node, pos) => {
                if (startPos !== null && endPos !== null) return false;

                if (node.isText && node.text) {
                  const textLength = node.text.length;
                  const nextOffset = currentTextOffset + textLength;

                  // Find start position
                  if (startPos === null && currentTextOffset <= start && start < nextOffset) {
                    startPos = pos + (start - currentTextOffset);
                  }

                  // Find end position
                  if (endPos === null && currentTextOffset <= end && end <= nextOffset) {
                    endPos = pos + (end - currentTextOffset);
                  }

                  currentTextOffset += textLength;
                }
              });

              // Validate positions
              if (
                startPos === null ||
                endPos === null ||
                startPos < 0 ||
                endPos > doc.content.size ||
                startPos >= endPos
              ) {
                return DecorationSet.empty;
              }

              try {
                const decoration = Decoration.inline(startPos, endPos, {
                  class: 'bg-primary-200 text-surface-800 rounded px-0.5',
                  'data-magic-highlight': 'true',
                });

                return DecorationSet.create(doc, [decoration]);
              } catch (error) {
                console.warn('Failed to create highlight decoration:', error);
                return DecorationSet.empty;
              }
            },
          },
        }),
      ];
    },
  });
}

// Toolbar button component
function ToolbarButton({
  onClick,
  isActive,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-sm transition-colors cursor-pointer ${
        isActive ? 'bg-primary-600 text-white' : 'hover:bg-surface-100 text-surface-600'
      } ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
      type="button"
      title={title}
    >
      {children}
    </button>
  );
}

// Toolbar separator
function ToolbarSeparator() {
  return <div className="w-px h-5 bg-surface-200 mx-1" />;
}

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      content,
      onChange,
      onSelectionChange,
      onFocus,
      onBlur,
      className = '',
      showToolbar = false,
    },
    ref
  ) => {
    // Each editor instance has its own highlight range
    const highlightRangeRef = useRef<{ start: number; end: number } | null>(null);
    // Tracks the last content emitted by the editor's own onUpdate (internal changes).
    // When the content prop differs from this, it's an external change (undo/redo, Magic Edit)
    // that needs to be pushed into the editor.
    const lastEmittedContentRef = useRef(content);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          // Disable trailing node to prevent forced empty paragraph at end
          trailingNode: false,
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        FontSize,
        createMagicEditHighlightExtension(() => highlightRangeRef.current),
      ],
      content,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        lastEmittedContentRef.current = html;
        onChange(html);
      },
      onFocus,
      onBlur,
      editorProps: {
        attributes: {
          class: className || 'outline-none',
          role: 'textbox',
        },
        handleDOMEvents: {
          // Only capture selection on mouseup, not during drag
          mouseup: (view) => {
            if (!onSelectionChange) return false;

            // Use setTimeout to let TipTap finish processing the mouseup first
            setTimeout(() => {
              const { from, to, empty } = view.state.selection;

              // Clear selection if nothing selected
              if (empty) {
                onSelectionChange({
                  from,
                  to,
                  startOffset: 0,
                  endOffset: 0,
                  selectedText: '',
                  isEmpty: true,
                });
                return;
              }

              // Get the actual selected text without block separators
              const selectedText = view.state.doc.textBetween(from, to, '');

              // Ignore whitespace-only selections
              if (!selectedText.trim()) {
                return;
              }

              // Calculate plain text offsets by counting characters before 'from'
              let startOffset = 0;

              // Traverse the document and count all text characters before 'from'
              view.state.doc.nodesBetween(0, from, (node: Node, pos: number) => {
                if (node.isText && node.text) {
                  const nodeEnd = pos + node.text.length;
                  if (nodeEnd <= from) {
                    // Entire text node is before selection start
                    startOffset += node.text.length;
                  } else {
                    // Selection starts within this text node
                    startOffset += from - pos;
                  }
                }
              });

              const endOffset = startOffset + selectedText.length;

              onSelectionChange({
                from,
                to,
                startOffset,
                endOffset,
                selectedText,
                isEmpty: false,
              });
            }, 0);

            return false; // Don't prevent default
          },
        },
      },
    });

    // Sync content prop into editor when it changes externally (undo/redo, Magic Edit, AI Edit).
    // Skips when the change came from the editor's own typing (tracked via lastEmittedContentRef).
    useEffect(() => {
      if (!editor) return;
      if (content === lastEmittedContentRef.current) return; // internal change, already in sync
      lastEmittedContentRef.current = content;
      const { from, to } = editor.state.selection;
      editor.commands.setContent(content, { emitUpdate: false });
      // Try to restore selection after content update
      try {
        editor.commands.setTextSelection({
          from: Math.min(from, editor.state.doc.content.size),
          to: Math.min(to, editor.state.doc.content.size),
        });
      } catch {
        // Selection restore failed, ignore
      }
    }, [content, editor]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getEditor: () => editor,
      getText: () => editor?.getText() || '',
      getSelection: () => {
        if (!editor) return null;

        const { from, to, empty } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to, '');

        // Calculate plain text offsets (same logic as mouseup)
        let startOffset = 0;

        editor.state.doc.nodesBetween(0, from, (node: Node, pos: number) => {
          if (node.isText && node.text) {
            const nodeEnd = pos + node.text.length;
            if (nodeEnd <= from) {
              // Entire text node is before selection start
              startOffset += node.text.length;
            } else {
              // Selection starts within this text node
              startOffset += from - pos;
            }
          }
        });

        return {
          from,
          to,
          startOffset,
          endOffset: startOffset + selectedText.length,
          selectedText,
          isEmpty: empty,
        };
      },
      setHighlight: (range: { start: number; end: number } | null) => {
        if (!editor) return;

        // Update this editor instance's highlight range
        highlightRangeRef.current = range;

        // Force decoration update by creating a new transaction
        // This ensures the decoration plugin re-runs
        const tr = editor.state.tr;
        editor.view.dispatch(tr);
      },
    }));

    if (!editor) {
      return null;
    }

    // Get current font size from text style
    const currentFontSize = editor.getAttributes('textStyle').fontSize;

    return (
      <div className="tiptap-editor">
        {showToolbar && (
          <div className="tiptap-toolbar border-b border-surface-200 p-2 flex items-center gap-1 flex-wrap bg-surface-50">
            {/* Text Formatting */}
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Italic (Ctrl+I)"
            >
              <Italic className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleStrike().run()}
              isActive={editor.isActive('strike')}
              title="Strikethrough"
            >
              <Strikethrough className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarSeparator />

            {/* Text Color */}
            <ColorPicker
              value={editor.getAttributes('textStyle').color}
              onChange={(color) => {
                if (color) {
                  editor.chain().focus().setColor(color).run();
                } else {
                  editor.chain().focus().unsetColor().run();
                }
              }}
              label="Text Color"
              icon={<Type className="w-4 h-4" />}
            />

            {/* Highlight Color */}
            <ColorPicker
              value={editor.getAttributes('highlight').color}
              onChange={(color) => {
                if (color) {
                  editor.chain().focus().toggleHighlight({ color }).run();
                } else {
                  editor.chain().focus().unsetHighlight().run();
                }
              }}
              label="Highlight"
              icon={<Highlighter className="w-4 h-4" />}
            />

            {/* Font Size */}
            <FontSizeDropdown
              value={currentFontSize}
              onChange={(size) => {
                // Custom command defined in FontSize extension
                (editor.chain().focus() as unknown as { setFontSize: (size: string) => { run: () => boolean } }).setFontSize(size).run();
              }}
            />

            <ToolbarSeparator />

            {/* Headings */}
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              isActive={editor.isActive('heading', { level: 1 })}
              title="Heading 1"
            >
              <Heading1 className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
              title="Heading 2"
            >
              <Heading2 className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              isActive={editor.isActive('heading', { level: 3 })}
              title="Heading 3"
            >
              <Heading3 className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarSeparator />

            {/* Lists */}
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              title="Bullet List"
            >
              <List className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
              title="Numbered List"
            >
              <ListOrdered className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarSeparator />

            {/* Clear Formatting */}
            <ToolbarButton
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              title="Clear Formatting"
            >
              <RemoveFormatting className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarSeparator />

            {/* Undo/Redo */}
            <ToolbarButton
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4" />
            </ToolbarButton>

            <ToolbarButton
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Redo (Ctrl+Y)"
            >
              <Redo2 className="w-4 h-4" />
            </ToolbarButton>
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';
